import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const opioEn: ChauffeurPage = {
  slug: "opio",
  title: "VTC & private chauffeur in Opio",
  subtitle:
    "Private chauffeur in Opio: the Grande Bastide golf course and olive groves in the Valbonne back country, with Nice airport transfers.",
  keywords: [
    "vtc opio",
    "private chauffeur opio",
    "opio airport transfer",
    "grande bastide golf chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Opio",
    subtitle:
      "The village of olive groves and the Grande Bastide golf course, in the Valbonne back country: Nice airport transfers and hourly hire.",
    keywords: [
      "VTC Opio",
      "Grande Bastide golf chauffeur",
      "Nice airport transfer",
      "Valbonne back country chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Opio",
    badges: [
      "Golf & olive groves",
      "Valbonne back country",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Opio",
    items: [
      {
        title: "Grande Bastide golf course",
        description:
          "Transfers to the golf course and nearby courses, with room for the bags and timing set around your tee-off, with no parking constraint.",
      },
      {
        title: "Olive groves & art of living",
        description:
          "A village of olive trees and oil mills, away from the bustle: a dedicated chauffeur for your travel at the back-country pace.",
      },
      {
        title: "Nice airport ~40 min",
        description:
          "Reach Nice Côte d’Azur airport in about 40 minutes via Valbonne and the A8, with flight tracking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "FR/EN, based on the French Riviera, punctual and discreet — 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Opio",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up in Opio with flight tracking, to and from Nice Côte d’Azur airport, in about 40 minutes.",
      },
      {
        title: "Golf transfers",
        text: "A link to the Grande Bastide golf course and the region’s courses, with luggage space for the bags and timing set around tee-off.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day: Valbonne, Grasse, Sophia Antipolis, oil mills and the villages of the back country.",
      },
      {
        title: "Sophia Antipolis link",
        text: "Journeys to the technology park and neighbouring companies, for business meetings and professional travel.",
      },
      {
        title: "Long distance",
        text: "Journeys of any distance from Opio: Cannes, Nice, Monaco, Italy and ski resorts, in a comfortable vehicle.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Grande Bastide golf course",
        description:
          "The Opio–Valbonne course, tee-off and pick-up timed to play.",
      },
      {
        name: "Opio village",
        description: "Heart of the village, olive groves and oil mills.",
      },
      {
        name: "Valbonne",
        description: "Neighbouring grid-plan village, a few minutes away.",
      },
      {
        name: "Sophia Antipolis",
        description: "The technology park nearby, companies and laboratories.",
      },
      {
        name: "Grasse",
        description: "The perfume capital, reachable through the back country.",
      },
      {
        name: "Nice airport",
        description: "About 40 minutes via Valbonne and the A8.",
      },
    ],
  },
  generic: {
    heading: "Opio, olive groves and golf of the back country",
    subheading:
      "A village of olive trees and golf, in the Valbonne back country, quiet yet well connected.",
    paragraphs: [
      "Opio cultivates a quiet art of living, between olive groves and oil mills, away from the coastal bustle. Its Grande Bastide golf course makes it a favourite with enthusiasts: the chauffeur provides the link with room for the bags and timing set around tee-off, with no parking constraint.",
      "The village is also well connected to the active back country: Valbonne, the Sophia Antipolis technology park and Grasse are within reach by road, for a business meeting as much as for an outing. Our chauffeurs shape the itinerary to your programme.",
      "For Nice Côte d’Azur airport, allow around 40 minutes via Valbonne and the A8 depending on traffic; our bilingual chauffeurs track your flight and adjust the departure.",
    ],
    bullets: [
      "Golf transfer with bags",
      "Sophia Antipolis & Valbonne link",
      "Flight tracking & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Opio",
    steps: [
      "Share your route (golf, Sophia Antipolis, Nice airport) and your times.",
      "We confirm the right vehicle and a fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you at the agreed address.",
      "Live adjustments to suit traffic and your tee-off time.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Opio"),
    {
      q: "How long is it to Nice airport?",
      a: "Allow about 40 minutes via Valbonne and the A8 depending on traffic, with flight tracking and a personal welcome.",
    },
    {
      q: "Do you handle transfers to the Grande Bastide golf course?",
      a: "Yes, with a vehicle offering the luggage space needed for the bags and timing set around your tee-off, at Opio–Valbonne as well as the nearby courses.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the Sophia Antipolis events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class sedans, 7-seater van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Do you serve Sophia Antipolis and Valbonne from Opio?",
      a: "Yes, the technology park and Valbonne are a few minutes away; we provide transfers and hourly hire for your business meetings across the whole area.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Book an airport transfer", href: "/en/chauffeur/airport" },
    { label: "Chauffeur hire Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Car rental with delivery in Grasse",
      href: "/en/location/car-rental-grasse",
    },
  ],
  cta: {
    title: "Your private chauffeur in Opio",
    text: "Nice airport transfer, golf link or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Opio — car service & airport transfer",
    description:
      "Private car service in Opio: Nice airport transfer in ~40 min, Grande Bastide golf course, olive groves and Valbonne back country. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/opio",
  },
};

export default opioEn;
